<template>
  <div class="crowdfunding-payment-page">
    <div class="container">
      <!-- ページタイトル -->
      <div class="page-header">
        <h1 class="page-title">応援支援購入</h1>
        <p class="page-subtitle">{{ projectTitle || "プロジェクト" }}</p>
      </div>

      <!-- エラーメッセージ -->
      <div v-if="error" class="error-message">
        <p>{{ error.message || error }}</p>
      </div>

      <!-- 成功メッセージ -->
      <div v-if="paymentSuccess" class="success-message">
        <h2>ご支援ありがとうございました！</h2>
        <div class="success-content">
          <p class="text-center">
            ご支援いただいた内容<br />
            <strong v-if="selectedReturn">{{ selectedReturn.name }}</strong
            ><br />
            支援金額: {{ formatCurrency(supportAmount) }}円
          </p>
          <p class="text-center mt-3">
            ご支援いただいた内容は、プロジェクトの成功に向けて大切に活用させていただきます。
          </p>
        </div>
        <button class="back-button" @click="goToCampaign">キャンペーンページに戻る</button>
      </div>

      <!-- 成功状態の確認（デバッグ用） -->
      <div v-if="paymentSuccess" style="display: none">
        Payment Success: {{ paymentSuccess }}, Order ID: {{ orderId }}
      </div>

      <!-- ローディング中 -->
      <div v-if="isLoading" class="loading-container">
        <p>読み込み中...</p>
      </div>

      <!-- メインコンテンツ -->
      <div v-if="!paymentSuccess && !isLoading" class="payment-content">
        <div class="payment-layout">
          <!-- 左側：注文内容 -->
          <div class="order-summary">
            <h2 class="section-title">注文内容</h2>

            <!-- プロジェクト情報 -->
            <div class="project-info-card">
              <div class="project-header">
                <img
                  v-if="iconURL"
                  :src="iconURL"
                  :alt="vendor_ja || 'VTuber'"
                  class="project-icon"
                />
                <div class="project-header-text">
                  <h3 class="project-name">{{ vendor_ja || "配信者名" }}</h3>
                  <p class="project-title-text">{{ projectTitle || "プロジェクトタイトル" }}</p>
                </div>
              </div>
            </div>

            <!-- 選択したリターン -->
            <div v-if="selectedReturn" class="return-item-card">
              <h3 class="return-item-title">選択したリターン</h3>
              <div class="return-item-content">
                <div v-if="selectedReturn.image" class="return-item-image">
                  <img :src="selectedReturn.image" :alt="selectedReturn.name" />
                </div>
                <div class="return-item-details">
                  <h4 class="return-item-name">{{ selectedReturn.name }}</h4>
                  <p class="return-item-price">{{ formatCurrency(selectedReturn.price) }}円</p>
                  <div
                    v-if="selectedReturn.description"
                    class="return-item-description"
                    v-html="sanitizeHtml(selectedReturn.description)"
                  ></div>
                </div>
              </div>
            </div>

            <!-- 支援金額 -->
            <div class="support-amount-card">
              <div class="amount-row">
                <span class="amount-label">支援金額</span>
                <span class="amount-value">{{ formatCurrency(supportAmount) }}円</span>
              </div>
            </div>

            <!-- 合計金額 -->
            <div class="total-amount-card">
              <div class="total-row">
                <span class="total-label">合計金額</span>
                <span class="total-value">{{ formatCurrency(supportAmount) }}円</span>
              </div>
            </div>
          </div>

          <!-- 右側：決済情報 -->
          <div class="payment-form">
            <h2 class="section-title">決済情報</h2>

            <!-- メッセージ入力（オプション） -->
            <div class="message-section">
              <label for="support-message" class="form-label">応援メッセージ（任意）</label>
              <textarea
                id="support-message"
                v-model="supportMessage"
                class="message-input"
                rows="4"
                placeholder="応援メッセージを入力してください"
                maxlength="500"
              ></textarea>
              <p class="character-count">{{ supportMessage.length }}/500</p>
              <p class="message-note">
                ※ご記載いただいた応援メッセージは、プロジェクトページとコメントへ投稿されます。
              </p>
            </div>

            <!-- 住所入力（配送が必要な場合、またはpaidy決済の場合） -->
            <div v-if="requiresShippingAddress || selectedPaymentMethod === 'paidy'" class="address-section">
              <h3 class="address-section-title">お届け先住所</h3>

              <!-- 登録済み住所の選択 -->
              <div v-if="!showAddressForm && addresses.length > 0" class="addresses-list">
                <div
                  v-for="address in addresses"
                  :key="address.id"
                  class="address-option"
                  :class="{ selected: selectedAddress?.id === address.id }"
                  @click="selectedAddress = address"
                >
                  <div class="address-info">
                    <div class="address-name">{{ address.name }}</div>
                    <div class="address-details">
                      〒{{ address.postNumber || address.postcode }}<br />
                      {{ address.address01 || address.address1 }}<br />
                      <span v-if="address.address02 || address.address2"
                        >{{ address.address02 || address.address2 }}<br
                      /></span>
                      TEL: {{ address.phoneNumber || address.phoneno }}
                    </div>
                  </div>
                  <div v-if="selectedAddress?.id === address.id" class="address-checkmark">✓</div>
                </div>
              </div>

              <!-- 新しい住所を追加 -->
              <div v-if="!showAddressForm" class="address-actions">
                <button class="add-address-button" @click="showAddressForm = true">
                  + 新しい住所を追加
                </button>
              </div>
            </div>

            <!-- 支払い方法 -->
            <div class="payment-method-section">
              <h3 class="payment-method-title">決済方法を選択</h3>

              <!-- 決済方法の選択 -->
              <div class="payment-method-options">
                <button
                  class="payment-method-option"
                  :class="{ selected: selectedPaymentMethod === 'creditcard' }"
                  @click="selectedPaymentMethod = 'creditcard'"
                >
                  <div class="payment-method-icon">💳</div>
                  <div class="payment-method-text">
                    <div class="payment-method-name">クレジットカード</div>
                  </div>
                  <div v-if="selectedPaymentMethod === 'creditcard'" class="payment-checkmark">
                    ✓
                  </div>
                </button>

                <button
                  class="payment-method-option"
                  :class="{ selected: selectedPaymentMethod === 'paidy' }"
                  @click="selectedPaymentMethod = 'paidy'"
                >
                  <div class="payment-method-icon">
                    <img src="/img/paidy.png" alt="Paidy" class="paidy-icon" />
                  </div>
                  <div class="payment-method-text">
                    <div class="payment-method-name">あと払い（ペイディ）</div>
                    <div class="payment-method-subtitle">Paidy</div>
                  </div>
                  <div v-if="selectedPaymentMethod === 'paidy'" class="payment-checkmark">✓</div>
                </button>
              </div>

              <!-- クレジットカード選択（クレジットカードが選択されている場合のみ表示） -->
              <div v-if="selectedPaymentMethod === 'creditcard'" class="credit-card-section">
                <div v-if="isLoadingCards" class="loading-cards">
                  <p>カード情報を読み込み中...</p>
                </div>

                <div v-else-if="cardsInfo && cardsInfo.length > 0" class="cards-list">
                  <div
                    v-for="card in cardsInfo"
                    :key="card.id"
                    class="card-option"
                    :class="{ selected: selectedCardId === card.id }"
                    @click="selectCard(card.id)"
                  >
                    <div class="card-info">
                      <div class="card-brand">
                        <span v-if="(card.card?.brand || card.brand) === 'visa'">Visa</span>
                        <span v-else-if="(card.card?.brand || card.brand) === 'mastercard'"
                          >Mastercard</span
                        >
                        <span v-else-if="(card.card?.brand || card.brand) === 'amex'"
                          >American Express</span
                        >
                        <span v-else-if="(card.card?.brand || card.brand) === 'jcb'">JCB</span>
                        <span v-else-if="(card.card?.brand || card.brand) === 'diners'"
                          >Diners Club</span
                        >
                        <span v-else>{{ card.card?.brand || card.brand }}</span>
                      </div>
                      <div class="card-number">
                        **** **** **** {{ card.card?.last4 || card.last4 }}
                      </div>
                      <div class="card-expiry">
                        {{ card.card?.exp_month || card.exp_month }}/{{
                          card.card?.exp_year || card.exp_year
                        }}
                      </div>
                    </div>
                    <div v-if="selectedCardId === card.id" class="card-checkmark">✓</div>
                  </div>
                </div>

                <div v-else class="no-cards">
                  <p>登録済みのカードがありません</p>
                </div>

                <!-- 新しいカードを追加 -->
                <button class="add-card-button" @click="showAddCardDialog = true">
                  + 新しいカードを追加
                </button>
              </div>

              <!-- Paidyエラーメッセージ -->
              <div v-if="selectedPaymentMethod === 'paidy' && paidyError" class="paidy-error">
                <p>Paidy決済でエラーが発生しました。もう一度お試しください。</p>
              </div>
            </div>

            <!-- 決済ボタン -->
            <div v-if="!paymentSuccess" class="payment-actions">
              <button
                v-if="selectedPaymentMethod === 'creditcard'"
                class="payment-button"
                :disabled="!canProceed || isProcessing"
                @click="processPayment"
              >
                <span v-if="isProcessing">処理中...</span>
                <span v-else>{{ formatCurrency(supportAmount) }}円を支援する</span>
              </button>
              <button
                v-else-if="selectedPaymentMethod === 'paidy'"
                class="payment-button paidy-button"
                :disabled="!canProceed || isProcessing"
                @click="processPaidyPayment"
              >
                <span v-if="isProcessing">処理中...</span>
                <span v-else>Paidyで{{ formatCurrency(supportAmount) }}円を支援する</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- カード追加ダイアログ -->
      <div v-if="showAddCardDialog" class="dialog-overlay" @click="showAddCardDialog = false">
        <div class="dialog-content" @click.stop>
          <h3 class="dialog-title">新しいカードを追加</h3>
          <div id="card-element" class="card-element"></div>
          <div class="dialog-actions">
            <button class="dialog-button cancel" @click="showAddCardDialog = false">
              キャンセル
            </button>
            <button class="dialog-button confirm" @click="addNewCard">追加</button>
          </div>
        </div>
      </div>

      <!-- 住所追加ダイアログ -->
      <div v-if="showAddressForm" class="dialog-overlay" @click="showAddressForm = false">
        <div class="dialog-content address-dialog-content" @click.stop>
          <h3 class="dialog-title">新しい住所を追加</h3>
          <div class="address-form">
            <div class="form-field">
              <label class="form-label">郵便番号</label>
              <input
                v-model="postcode"
                type="text"
                class="form-input"
                placeholder="1234567"
                maxlength="7"
                @input="getaddressfrompostcode"
              />
              <p class="form-hint">※郵便番号を入力すると住所は自動で入力されます</p>
              <p v-if="postcodeError" class="form-error">{{ postcodeError }}</p>
            </div>

            <div class="form-field">
              <label class="form-label">都道府県</label>
              <input v-model="todohuken" type="text" class="form-input" required />
            </div>

            <div class="form-field">
              <label class="form-label">市区町村・番地</label>
              <input v-model="moreaddress" type="text" class="form-input" required />
            </div>

            <div class="form-field">
              <label class="form-label">建物名・部屋番号</label>
              <input v-model="address2" type="text" class="form-input" />
            </div>

            <div class="form-field">
              <label class="form-label">姓</label>
              <input v-model="familyname" type="text" class="form-input" required />
            </div>

            <div class="form-field">
              <label class="form-label">名</label>
              <input v-model="firstname" type="text" class="form-input" required />
            </div>

            <div class="form-field">
              <label class="form-label">電話番号</label>
              <input
                v-model="phoneno"
                type="text"
                class="form-input"
                placeholder="09012345678"
                @input="checkphoneno"
                required
              />
              <p v-if="phoneError" class="form-error">{{ phoneError }}</p>
            </div>

            <div class="dialog-actions">
              <button class="dialog-button cancel" @click="showAddressForm = false">
                キャンセル
              </button>
              <button class="dialog-button confirm" @click="sendaddress">保存</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, getDoc, collection, getDocs, onSnapshot } from "firebase/firestore";
import DOMPurify from "dompurify";
import db from "../main.js";
import { stripe } from "../stripe.js";
import axios from "axios";

const route = useRoute();
const router = useRouter();

// ルートパラメータとクエリから取得
const campaignId = ref(route.params.campaignId || route.query.campaignId);
const returnId = ref(route.query.returnId);
const supportAmount = ref(0); // 金額はサーバー側から取得（セキュリティのため）

// プロジェクト情報
const projectTitle = ref("");
const vendor_ja = ref("");
const iconURL = ref(null);
const baseColor = ref("");

// 選択したリターン
const selectedReturn = ref(null);
const requiresShippingAddress = ref(false);

// 住所関連
const addresses = ref([]);
const selectedAddress = ref(null);
const showAddressForm = ref(false);
const postcode = ref(null);
const todohuken = ref(null);
const moreaddress = ref(null);
const address1 = ref(null);
const address2 = ref(null);
const familyname = ref("");
const firstname = ref("");
const name = ref(null);
const phoneno = ref(null);
const postcodeError = ref(null);
const phoneError = ref(null);

// フォームデータ
const supportMessage = ref("");
const selectedCardId = ref(null);
const cardsInfo = ref([]);
const defaultCard = ref(null);
const isLoadingCards = ref(true);
const showAddCardDialog = ref(false);
const cardElementInstance = ref(null);

// 状態管理
const isLoading = ref(true);
const isProcessing = ref(false);
const error = ref(null);
const orderId = ref(null);
const paymentSuccess = ref(false);
const selectedPaymentMethod = ref("creditcard"); // "creditcard" or "paidy"
const paidyPaymentId = ref(null);
const paidyError = ref(false);

// 計算プロパティ
const canProceed = computed(() => {
  // 住所が必要な場合のチェック（配送が必要な場合、またはpaidy決済の場合）
  if (requiresShippingAddress.value || selectedPaymentMethod.value === "paidy") {
    if (!selectedAddress.value && !showAddressForm.value) {
      return false;
    }
    if (showAddressForm.value) {
      // 住所フォームが表示されている場合、必須項目をチェック
      if (
        !postcode.value ||
        !todohuken.value ||
        !moreaddress.value ||
        !familyname.value ||
        !firstname.value ||
        !phoneno.value
      ) {
        return false;
      }
    }
  }

  if (selectedPaymentMethod.value === "creditcard") {
    return selectedCardId.value !== null && supportAmount.value > 0 && !isProcessing.value;
  } else if (selectedPaymentMethod.value === "paidy") {
    return supportAmount.value > 0 && !isProcessing.value;
  }
  return false;
});

// 金額フォーマット
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("ja-JP").format(amount);
};

// HTMLをサニタイズする関数
const sanitizeHtml = (html) => {
  if (!html) return "";
  // 基本的なHTMLタグを許可（br, p, strong, em, ul, ol, li, aなど）
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["br", "p", "strong", "em", "u", "b", "i", "ul", "ol", "li", "a", "span", "div"],
    ALLOWED_ATTR: ["href", "target", "rel"],
  });
};

// キャンペーンデータを取得
const fetchCampaignData = async () => {
  try {
    if (!campaignId.value) {
      error.value = "キャンペーンIDが指定されていません";
      isLoading.value = false;
      return;
    }

    const campaignDoc = await getDoc(doc(db, "Crowdfunding_projects", campaignId.value));
    if (!campaignDoc.exists()) {
      error.value = "キャンペーンが見つかりません";
      isLoading.value = false;
      return;
    }

    const data = campaignDoc.data();
    projectTitle.value = data.projectTitle || "";
    vendor_ja.value = data.vendor_ja || "";
    iconURL.value = data.iconURL || null;
    baseColor.value = data.baseColor || "";

    // リターン情報を取得（サブコレクションから）
    if (returnId.value) {
      const returnDoc = await getDoc(
        doc(db, "Crowdfunding_projects", campaignId.value, "returns", returnId.value)
      );
      if (returnDoc.exists()) {
        const returnData = returnDoc.data();
        selectedReturn.value = {
          id: returnDoc.id,
          name: returnData.title || "",
          price: returnData.price || 0,
          description: returnData.description || "",
          image: returnData.thumbUrl || null,
        };
        requiresShippingAddress.value = returnData.requiresShippingAddress || false;
        // 金額は必ずサーバー側から取得（セキュリティのため）
        supportAmount.value = selectedReturn.value.price || 0;
      }
    }

    isLoading.value = false;
  } catch (err) {
    console.error("Error fetching campaign data:", err);
    error.value = "データの取得に失敗しました";
    isLoading.value = false;
  }
};

// 住所データを取得（既存のパターンに合わせる）
const getAddressData = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      console.log("User not authenticated");
      return;
    }

    addresses.value = [];
    const querySnapshot = await getDocs(collection(db, "users", user.uid, "addresses"));
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      addresses.value.push({
        id: doc.id,
        addressId: doc.id, // addressIdも追加（既存のパターンに合わせる）
        ...data,
      });
    });
    console.log("Addresses loaded:", addresses.value.length);
  } catch (err) {
    console.error("Error fetching addresses:", err);
    error.value = "住所の取得に失敗しました";
  }
};

// 住所を追加
const sendaddress = async () => {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) return;

    address1.value = todohuken.value + moreaddress.value;
    name.value = familyname.value + " " + firstname.value;

    const functions = getFunctions();
    functions.region = "asia-northeast1";
    const addMessage = httpsCallable(functions, "Goods_Addaddress");
    await addMessage({
      postcode: postcode.value,
      address1: address1.value,
      address2: address2.value,
      name: name.value,
      phoneno: phoneno.value,
    });

    await getAddressData();
    showAddressForm.value = false;
    // 新しく追加した住所を選択
    const newAddress = addresses.value[addresses.value.length - 1];
    if (newAddress) {
      selectedAddress.value = newAddress;
    }
  } catch (err) {
    console.error("Error adding address:", err);
    error.value = "住所の追加に失敗しました";
  }
};

// 郵便番号から住所を取得
const jsonpAdapter = require("axios-jsonp");
const getaddressfrompostcode = () => {
  const apiURL = "https://api.zipaddress.net/?zipcode=" + postcode.value;

  if (!postcode.value || postcode.value.toString().length != 7) {
    postcodeError.value = "郵便番号はハイフンなしの7桁で入力してください。";
  } else {
    postcodeError.value = null;
  }

  axios.get(apiURL, { adapter: jsonpAdapter }).then(async (res) => {
    const response = res.data;
    todohuken.value = response.pref;
    if (response.city) {
      todohuken.value = response.pref;
      moreaddress.value = response.city + response.town;
    } else {
      postcodeError.value = "郵便番号が正しくありません。";
    }
  });
};

// 電話番号のバリデーション
const checkphoneno = () => {
  if (!phoneno.value || phoneno.value.toString().length < 9) {
    phoneError.value = "電話番号を入力してください。";
  } else {
    phoneError.value = null;
  }
};

// クレジットカード情報を取得（既存のパターンに合わせる）
const getCreditCard = async () => {
  console.log("getCreditCard called");
  isLoadingCards.value = true;
  const functions = getFunctions();
  functions.region = "asia-northeast1";

  const myCallableFunction = httpsCallable(functions, "Common_cardslist");
  myCallableFunction({})
    .then((result) => {
      cardsInfo.value = result.data.cards || [];
      defaultCard.value = result.data.defaultCardId;

      console.log("Cards loaded:", cardsInfo.value.length, "Default card:", defaultCard.value);
      // カード情報の構造を確認
      if (cardsInfo.value.length > 0) {
        console.log("First card structure:", cardsInfo.value[0]);
        console.log("Card brand:", cardsInfo.value[0].card?.brand || cardsInfo.value[0].brand);
        console.log("Card last4:", cardsInfo.value[0].card?.last4 || cardsInfo.value[0].last4);
        console.log(
          "Card exp_month:",
          cardsInfo.value[0].card?.exp_month || cardsInfo.value[0].exp_month
        );
        console.log(
          "Card exp_year:",
          cardsInfo.value[0].card?.exp_year || cardsInfo.value[0].exp_year
        );
      }

      // デフォルトカードを選択
      if (defaultCard.value && cardsInfo.value.length > 0) {
        selectedCardId.value = defaultCard.value;
      } else if (cardsInfo.value.length > 0) {
        selectedCardId.value = cardsInfo.value[0].id;
      }

      isLoadingCards.value = false;
    })
    .catch((err) => {
      console.error("Error fetching credit cards:", err);
      console.log("Error message:", err.message);
      console.log("Error code:", err.code);
      error.value = err.message || "カード情報の取得に失敗しました";
      isLoadingCards.value = false;
      cardsInfo.value = [];
      defaultCard.value = null;
    });
};

// デフォルトアドレスとデフォルト決済方法の取得（既存のパターンに合わせる）
const defaultAddress = ref(false);
const defaultpaymentmethod = ref(null);
let unsubscribeDefaultAddress = null;

const getDefaultAddress = () => {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) {
    console.log("User not authenticated for getDefaultAddress");
    return;
  }

  console.log("Setting up default address listener");

  // 既存のリスナーをクリーンアップ
  if (unsubscribeDefaultAddress) {
    unsubscribeDefaultAddress();
  }

  unsubscribeDefaultAddress = onSnapshot(
    doc(db, "users", user.uid, "sub_accounts", "sub_accounts"),
    (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        defaultAddress.value = data.default_address;
        defaultpaymentmethod.value = data.default_paymentmethod;

        console.log(
          "Default address:",
          defaultAddress.value,
          "Default payment:",
          defaultpaymentmethod.value
        );

        // デフォルト住所がある場合は自動選択
        if (defaultAddress.value && addresses.value.length > 0) {
          const defaultAddr = addresses.value.find(
            (addr) => addr.addressId === defaultAddress.value || addr.id === defaultAddress.value
          );
          if (defaultAddr) {
            selectedAddress.value = defaultAddr;
            console.log("Default address selected:", defaultAddr);
          }
        }

        // デフォルト決済方法がある場合は自動選択
        if (defaultpaymentmethod.value === "creditcard" && defaultCard.value) {
          selectedPaymentMethod.value = "creditcard";
          selectedCardId.value = defaultCard.value;
          console.log("Default credit card selected");
        } else if (defaultpaymentmethod.value === "paidy") {
          selectedPaymentMethod.value = "paidy";
          console.log("Default payment method: Paidy");
        }
      } else {
        console.log("sub_accounts document does not exist");
      }
    },
    (error) => {
      console.error("Error in getDefaultAddress snapshot:", error);
    }
  );
};

// カードを選択
const selectCard = (cardId) => {
  selectedCardId.value = cardId;
};

// 新しいカードを追加
const addNewCard = async () => {
  try {
    if (!cardElementInstance.value) {
      error.value = "カード要素が初期化されていません";
      return;
    }

    isProcessing.value = true;
    error.value = null;

    const functions = getFunctions();
    functions.region = "asia-northeast1";

    // サーバーでSetupIntentを作成
    const createSetupIntent = httpsCallable(functions, "Common_CreateSetupIntent");
    const { data: setupIntentData } = await createSetupIntent();

    const { clientSecret } = setupIntentData;

    // クライアント側でカードを保存（郵便番号は不要）
    const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardElementInstance.value,
      },
    });

    if (stripeError) {
      console.error("カード保存エラー:", stripeError.message);
      error.value = "カード保存に失敗しました: " + stripeError.message;
      isProcessing.value = false;
      return;
    }

    console.log("カード保存成功:", setupIntent.payment_method);
    // カードリストを再取得
    await getCreditCard();
    showAddCardDialog.value = false;
    isProcessing.value = false;
  } catch (err) {
    console.error("Error adding card:", err);
    error.value = "カードの追加に失敗しました: " + err.message;
    isProcessing.value = false;
  }
};

// 決済処理（OutOfHoursPurchaseのパターンに合わせて段階的に実装）
const processPayment = async () => {
  if (!canProceed.value) return;

  try {
    isProcessing.value = true;
    error.value = null;

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      router.push({ path: "/login", query: { redirect: route.fullPath } });
      return;
    }

    const functions = getFunctions();
    functions.region = "asia-northeast1";

    // ステップ1: PaymentIntentを作成（オーソリ）
    const createPaymentIntent = httpsCallable(functions, "Crowdfunding_CreatePaymentIntent");

    const result = await createPaymentIntent({
      campaignId: campaignId.value,
      returnId: returnId.value,
      amount: supportAmount.value,
      paymentMethodId: selectedCardId.value,
      message: supportMessage.value,
    });

    const clientSecret = result.data.clientSecret;
    const paymentIntentId = result.data.paymentIntentId;
    // オーソリが成功

    // ステップ2: 3Dセキュア認証を実行
    try {
      const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: selectedCardId.value,
        setup_future_usage: "on_session", // サービス内でユーザーが即時購入する場合
      });

      if (confirmError) {
        console.error("3Dセキュア認証エラー:", confirmError.message);
        error.value = confirmError.message;
        isProcessing.value = false;
        return;
      }

      if (paymentIntent.status !== "requires_capture") {
        console.error("キャプチャ可能な状態ではありません:", paymentIntent.status);
        error.value = "決済処理に失敗しました";
        isProcessing.value = false;
        return;
      }

      // ステップ3: キャプチャ処理を実行
      const capturePayment = httpsCallable(functions, "Crowdfunding_CapturePayment");
      // 住所情報を取得
      let shippingAddress = null;
      if (requiresShippingAddress.value) {
        if (selectedAddress.value) {
          // 既存の住所を使用（フィールド名を統一）
          shippingAddress = {
            postcode: selectedAddress.value.postNumber || selectedAddress.value.postcode,
            address1: selectedAddress.value.address01 || selectedAddress.value.address1,
            address2: selectedAddress.value.address02 || selectedAddress.value.address2,
            name: selectedAddress.value.name,
            phoneno: selectedAddress.value.phoneNumber || selectedAddress.value.phoneno,
          };
        } else if (showAddressForm.value) {
          shippingAddress = {
            postcode: postcode.value,
            address1: address1.value || todohuken.value + moreaddress.value,
            address2: address2.value,
            name: name.value || familyname.value + " " + firstname.value,
            phoneno: phoneno.value,
          };
        }
      }

      const captureResult = await capturePayment({
        campaignId: campaignId.value,
        returnId: returnId.value,
        amount: supportAmount.value,
        paymentIntentId: paymentIntentId,
        message: supportMessage.value,
        shippingAddress: shippingAddress,
      });

      if (captureResult.data.status === "succeeded") {
        orderId.value = captureResult.data.stripeOrderId || captureResult.data.orderId;
        paymentSuccess.value = true;
        isProcessing.value = false;
        // 成功状態をURLに反映（ページリロードしても成功画面を表示）
        router.replace({
          path: route.path,
          query: {
            ...route.query,
            success: "true",
            orderId: orderId.value,
          },
        });
        // カード情報を再取得
        await getCreditCard();
      } else {
        alert("支払いキャプチャーに失敗しました: " + captureResult.data.status);
        error.value = "決済の確定に失敗しました";
        isProcessing.value = false;
      }
    } catch (err) {
      console.error("キャプチャー中にエラーが発生しました:", err.message);
      error.value = err.message;
      isProcessing.value = false;
    }
  } catch (err) {
    console.error("Error during authorization:", err.message);
    error.value = err.message;
    isProcessing.value = false;
  }
};

// カード要素の初期化
const initializeCardElement = async () => {
  if (showAddCardDialog.value) {
    // 既存のインスタンスをクリーンアップ
    if (cardElementInstance.value) {
      cardElementInstance.value.unmount();
      cardElementInstance.value = null;
    }

    // 少し待ってから初期化（DOMが更新されるのを待つ）
    await new Promise((resolve) => setTimeout(resolve, 100));

    const elements = stripe.elements();
    cardElementInstance.value = elements.create("card", {
      hidePostalCode: true, // 郵便番号フィールドを非表示
      style: {
        base: {
          fontSize: "16px",
          color: "#424770",
          "::placeholder": {
            color: "#aab7c4",
          },
        },
        invalid: {
          color: "#9e2146",
        },
      },
    });

    cardElementInstance.value.mount("#card-element");
  }
};

// ダイアログの表示状態を監視
watch(showAddCardDialog, (newVal) => {
  if (newVal) {
    initializeCardElement();
  } else {
    if (cardElementInstance.value) {
      cardElementInstance.value.unmount();
      cardElementInstance.value = null;
    }
  }
});

// Paidy決済処理
const processPaidyPayment = async () => {
  if (!canProceed.value) return;

  try {
    isProcessing.value = true;
    error.value = null;
    paidyError.value = false;

    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      router.push({ path: "/login", query: { redirect: route.fullPath } });
      return;
    }

    // Paidy決済用のデータを取得
    const functions = getFunctions();
    functions.region = "asia-northeast1";

    // Paidy決済用の情報を取得（必要に応じて実装）
    // ここでは簡易的にPaidy SDKを直接使用
    const paidypublickkey = process.env.VUE_APP_PAIDY_PUBLIC_KEY;
    if (!paidypublickkey) {
      error.value = "Paidyの設定が正しくありません";
      isProcessing.value = false;
      return;
    }

    // Paidyの設定
    const config = {
      api_key: paidypublickkey,
      closed: function (callbackData) {
        if (callbackData.status === "authorized") {
          // 支払いが承認されたらPayment IDを取得
          paidyPaymentId.value = callbackData.id;
          // Paidy Payment IDを使ってキャプチャ処理を実行
          capturePaidyPayment(paidyPaymentId.value);
        } else {
          paidyError.value = true;
          error.value = "Paidy決済が承認されませんでした";
          isProcessing.value = false;
        }
      },
    };

    const paidyHandler = window.Paidy.configure(config);

    // 住所情報を取得（paidy決済の場合は常に必要）
    let shippingAddress = null;
    if (selectedAddress.value) {
      // 既存の住所を使用
      shippingAddress = {
        postcode: selectedAddress.value.postNumber || selectedAddress.value.postcode,
        address1: selectedAddress.value.address01 || selectedAddress.value.address1,
        address2: selectedAddress.value.address02 || selectedAddress.value.address2,
        name: selectedAddress.value.name,
        phoneno: selectedAddress.value.phoneNumber || selectedAddress.value.phoneno,
      };
    } else if (showAddressForm.value) {
      // 住所フォームから取得
      address1.value = todohuken.value + moreaddress.value;
      name.value = familyname.value + " " + firstname.value;
      shippingAddress = {
        postcode: postcode.value,
        address1: address1.value,
        address2: address2.value,
        name: name.value,
        phoneno: phoneno.value,
      };
    }

    // 住所がない場合はエラー
    if (!shippingAddress) {
      error.value = "あと払い（ペイディ）決済にはお届け先住所が必要です。住所を選択または追加してください。";
      isProcessing.value = false;
      return;
    }

    // 住所をPaidy形式に変換
    const splitAddress = (fullAddress, address2) => {
      const prefectures = [
        "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
        "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
        "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県",
        "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県",
        "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県", "広島県", "山口県",
        "徳島県", "香川県", "愛媛県", "高知県", "福岡県", "佐賀県", "長崎県",
        "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県",
      ];

      let todohukenForPaidy = null;
      let cityForPaidy = null;
      let addressForPaidy = null;

      for (const prefecture of prefectures) {
        if (fullAddress.startsWith(prefecture)) {
          todohukenForPaidy = prefecture;
          fullAddress = fullAddress.slice(prefecture.length).trim();
          break;
        }
      }

      const cityIndex = fullAddress.search(/[市区町村郡]/);
      if (cityIndex !== -1) {
        cityForPaidy = fullAddress.slice(0, cityIndex + 1).trim();
        addressForPaidy = fullAddress.slice(cityIndex + 1).trim();
      } else {
        cityForPaidy = "";
        addressForPaidy = fullAddress;
      }

      const postCodeaddHyphenForPaidy =
        shippingAddress.postcode.toString().slice(0, 3) +
        "-" +
        shippingAddress.postcode.toString().slice(3);

      return {
        line1: address2 || shippingAddress.address2 || "",
        line2: addressForPaidy || shippingAddress.address1,
        city: cityForPaidy,
        state: todohukenForPaidy,
        zip: postCodeaddHyphenForPaidy,
      };
    };

    const paidyAddress = splitAddress(shippingAddress.address1, shippingAddress.address2);

    // Paidy決済用のペイロード
    const payload = {
      amount: supportAmount.value,
      currency: "JPY",
      store_name: "SukiSukiらいぶすてーしょん",
      buyer: {
        email: user.email,
        name1: shippingAddress.name || user.displayName || "ユーザー",
      },
      order: {
        items: [
          {
            quantity: 1,
            title: selectedReturn.value ? selectedReturn.value.name : projectTitle.value,
            unit_price: supportAmount.value,
          },
        ],
      },
      shipping_address: paidyAddress,
    };

    // Paidy Checkoutを起動
    paidyHandler.launch(payload);
  } catch (err) {
    console.error("Error processing Paidy payment:", err);
    error.value = err.message || "Paidy決済処理中にエラーが発生しました";
    paidyError.value = true;
    isProcessing.value = false;
  }
};

// Paidy決済のキャプチャ処理
const capturePaidyPayment = async (paidyPaymentIdValue) => {
  try {
    const functions = getFunctions();
    functions.region = "asia-northeast1";

    // クラウドファンディング用のPaidy決済関数を呼び出し
    // 住所情報を取得（paidy決済の場合は常に必要）
    let shippingAddress = null;
    if (selectedAddress.value) {
      // 既存の住所を使用（フィールド名を統一）
      shippingAddress = {
        postcode: selectedAddress.value.postNumber || selectedAddress.value.postcode,
        address1: selectedAddress.value.address01 || selectedAddress.value.address1,
        address2: selectedAddress.value.address02 || selectedAddress.value.address2,
        name: selectedAddress.value.name,
        phoneno: selectedAddress.value.phoneNumber || selectedAddress.value.phoneno,
      };
    } else if (showAddressForm.value) {
      address1.value = todohuken.value + moreaddress.value;
      name.value = familyname.value + " " + firstname.value;
      shippingAddress = {
        postcode: postcode.value,
        address1: address1.value,
        address2: address2.value,
        name: name.value,
        phoneno: phoneno.value,
      };
    }

    const capturePaidy = httpsCallable(functions, "Crowdfunding_PaidyCharge");
    const result = await capturePaidy({
      campaignId: campaignId.value,
      returnId: returnId.value,
      amount: supportAmount.value,
      paidyPaymentId: paidyPaymentIdValue,
      message: supportMessage.value,
      shippingAddress: shippingAddress,
    });

    if (result.data.status === "succeeded" || result.data.text === "完了した") {
      orderId.value = paidyPaymentIdValue;
      paymentSuccess.value = true;
      isProcessing.value = false;
      // 成功状態をURLに反映（ページリロードしても成功画面を表示）
      router.replace({
        path: route.path,
        query: {
          ...route.query,
          success: "true",
          orderId: orderId.value,
        },
      });
    } else {
      error.value = "Paidy決済の確定に失敗しました";
      paidyError.value = true;
      isProcessing.value = false;
    }
  } catch (err) {
    console.error("Error capturing Paidy payment:", err);
    error.value = err.message || "Paidy決済の確定中にエラーが発生しました";
    paidyError.value = true;
    isProcessing.value = false;
  }
};

// キャンペーンページに戻る
const goToCampaign = () => {
  router.push({
    path: `/crowdfunding/${campaignId.value}`,
  });
};

// ライフサイクル
onMounted(async () => {
  console.log("CrowdfundingPayment mounted");

  // URLパラメータから成功状態を確認
  if (route.query.success === "true" && route.query.orderId) {
    console.log("Payment success detected from URL");
    paymentSuccess.value = true;
    orderId.value = route.query.orderId;
    isLoading.value = false;
    // 成功画面を表示するため、データ取得はスキップ
    return;
  }

  // ユーザー認証を確認
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    console.log("No user, redirecting to login");
    router.push({ path: "/login", query: { redirect: route.fullPath } });
    return;
  }

  console.log("User authenticated:", user.uid);

  // データを順番に取得
  await fetchCampaignData();
  await getAddressData();
  await getCreditCard();
  getDefaultAddress(); // デフォルト情報の監視を開始
});

onUnmounted(() => {
  if (cardElementInstance.value) {
    cardElementInstance.value.unmount();
  }
  // リスナーをクリーンアップ
  if (unsubscribeDefaultAddress) {
    unsubscribeDefaultAddress();
  }
});
</script>

<style scoped>
.crowdfunding-payment-page {
  min-height: 100vh;
  background-color: #f5f5f5;
  padding: 2rem 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.page-header {
  text-align: center;
  margin-bottom: 2rem;
}

.page-title {
  font-size: 2rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 2rem;
}

.page-subtitle {
  font-size: 1.2rem;
  color: #666;
}

.error-message {
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 4px;
  padding: 1rem;
  margin-bottom: 1rem;
  color: #c33;
}

.success-message {
  background-color: #efe;
  border: 1px solid #cfc;
  border-radius: 4px;
  padding: 2rem;
  margin-bottom: 2rem;
  text-align: center;
  color: #3c3;
}

.success-message h2 {
  color: #3c3;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  line-height: 1.3;
}

.success-content {
  margin-bottom: 1.5rem;
}

.success-content p {
  color: #666;
  margin-bottom: 0.5rem;
  line-height: 1.6;
}

.success-content strong {
  color: #333;
  font-size: 1.1rem;
}

.text-center {
  text-align: center;
}

.mt-3 {
  margin-top: 1rem;
}

.back-button {
  margin-top: 1rem;
  padding: 0.75rem 2rem;
  background-color: #e91e63;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
}

.back-button:hover {
  background-color: #c2185b;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(233, 30, 99, 0.3);
}

.loading-container {
  text-align: center;
  padding: 3rem;
  color: #666;
}

.payment-content {
  margin-top: 2rem;
}

.payment-layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.order-summary,
.payment-form {
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #eee;
}

/* プロジェクト情報カード */
.project-info-card {
  margin-bottom: 1.5rem;
}

.project-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.project-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
}

.project-header-text {
  flex: 1;
}

.project-name {
  font-size: 1.2rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 0.25rem;
  text-align: left;
}

.project-title-text {
  font-size: 0.9rem;
  color: #666;
  text-align: left;
}

/* リターンアイテムカード */
.return-item-card {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.return-item-title {
  font-size: 1.1rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 1rem;
}

.return-item-content {
  display: flex;
  gap: 1rem;
}

.return-item-image {
  width: 120px;
  aspect-ratio: 640 / 360; /* 16:9の比率を維持 */
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.return-item-image img {
  width: 100%;
  height: 100%;
  object-fit: contain; /* 画像全体を表示 */
}

.return-item-details {
  flex: 1;
}

.return-item-name {
  font-size: 1rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 0.5rem;
}

.return-item-price {
  font-size: 1.2rem;
  font-weight: bold;
  color: #e91e63;
  margin-bottom: 0.5rem;
}

.return-item-description {
  font-size: 0.9rem;
  color: #666;
  line-height: 1.5;
  text-align: left;
}

/* 支援金額カード */
.support-amount-card {
  margin-bottom: 1rem;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.amount-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.amount-label {
  font-size: 1rem;
  color: #666;
}

.amount-value {
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
}

/* 合計金額カード */
.total-amount-card {
  padding: 1.5rem;
  background-color: #f0f0f0;
  border-radius: 8px;
  border: 2px solid #e0e0e0;
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.total-label {
  font-size: 1.2rem;
  font-weight: bold;
  color: #333;
}

.total-value {
  font-size: 2rem;
  font-weight: bold;
  color: #e91e63;
}

/* メッセージセクション */
.message-section {
  margin-bottom: 2rem;
}

.form-label {
  display: block;
  font-size: 1rem;
  font-weight: bold;
  text-align: left;
  color: #333;
  margin-bottom: 0.5rem;
}

.message-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
}

.message-note {
  font-size: 0.85rem;
  color: #666;
  margin-top: 0.5rem;
  line-height: 1.4;
}

.character-count {
  text-align: right;
  font-size: 0.85rem;
  color: #999;
  margin-top: 0.25rem;
}

/* 支払い方法セクション */
.payment-method-section {
  margin-bottom: 2rem;
}

.payment-method-title {
  font-size: 1.1rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 1rem;
}

.payment-method-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.payment-method-option {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border: 2px solid #ddd;
  border-radius: 8px;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
}

.payment-method-option:hover {
  border-color: #999;
  background-color: #f9f9f9;
}

.payment-method-option.selected {
  border-color: #e91e63;
  background-color: #fff5f8;
}

.payment-method-icon {
  font-size: 2rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.paidy-icon {
  width: 40px;
  height: auto;
}

.payment-method-text {
  flex: 1;
}

.payment-method-name {
  font-size: 1rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 0.25rem;
}

.payment-method-subtitle {
  font-size: 0.85rem;
  color: #666;
}

.payment-checkmark {
  font-size: 1.5rem;
  color: #e91e63;
  font-weight: bold;
}

.credit-card-section {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
}

.paidy-error {
  margin-top: 1rem;
  padding: 0.75rem;
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 4px;
  color: #c33;
  font-size: 0.9rem;
}

.paidy-button {
  background: linear-gradient(90deg, #4a90e2 0%, #357abd 100%);
}

.paidy-button:hover:not(:disabled) {
  background: linear-gradient(90deg, #357abd 0%, #2a5f8f 100%);
}

/* 住所セクション */
.address-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.address-section-title {
  font-size: 1.1rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 1rem;
}

.addresses-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.address-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 2px solid #ddd;
  border-radius: 8px;
  background-color: white;
  cursor: pointer;
  transition: all 0.2s;
}

.address-option:hover {
  border-color: #999;
  background-color: #f9f9f9;
}

.address-option.selected {
  border-color: #e91e63;
  background-color: #fff5f8;
}

.address-info {
  flex: 1;
}

.address-name {
  font-weight: bold;
  color: #333;
  margin-bottom: 0.5rem;
}

.address-details {
  font-size: 0.9rem;
  color: #666;
  line-height: 1.6;
}

.address-checkmark {
  font-size: 1.5rem;
  color: #e91e63;
  font-weight: bold;
}

.address-actions {
  margin-top: 1rem;
}

.add-address-button {
  width: 100%;
  padding: 0.75rem;
  background-color: white;
  border: 2px dashed #ddd;
  border-radius: 8px;
  color: #666;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
}

.add-address-button:hover {
  border-color: #999;
  background-color: #f9f9f9;
}

.address-form {
  margin-top: 1rem;
  padding: 1rem;
  background-color: white;
  border-radius: 8px;
}

.dialog-content .address-form {
  margin-top: 0;
  padding: 0;
  background-color: transparent;
}

.form-field {
  margin-bottom: 1rem;
  text-align: left;
}

.form-hint {
  font-size: 0.85rem;
  color: #999;
  margin-top: 0.25rem;
}

.form-error {
  font-size: 0.85rem;
  color: #c33;
  margin-top: 0.25rem;
}

.form-input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  font-family: inherit;
}

.form-input:focus {
  outline: none;
  border-color: #e91e63;
}

.address-form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.cancel-button,
.save-button {
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-button {
  background-color: #f5f5f5;
  color: #666;
}

.cancel-button:hover {
  background-color: #e0e0e0;
}

.save-button {
  background-color: #e91e63;
  color: white;
}

.save-button:hover {
  background-color: #c2185b;
}

.loading-cards,
.no-cards {
  text-align: center;
  padding: 2rem;
  color: #666;
}

.cards-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1rem;
}

.card-option {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 2px solid #ddd;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.card-option:hover {
  border-color: #999;
  background-color: #f9f9f9;
}

.card-option.selected {
  border-color: #e91e63;
  background-color: #fff5f8;
}

.card-info {
  flex: 1;
}

.card-brand {
  font-weight: bold;
  color: #333;
  margin-bottom: 0.25rem;
  text-transform: capitalize;
}

.card-number {
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 0.25rem;
}

.card-expiry {
  font-size: 0.85rem;
  color: #999;
}

.card-checkmark {
  font-size: 1.5rem;
  color: #e91e63;
  font-weight: bold;
}

.add-card-button {
  width: 100%;
  padding: 0.75rem;
  background-color: white;
  border: 2px dashed #ddd;
  border-radius: 8px;
  color: #666;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
}

.add-card-button:hover {
  border-color: #999;
  background-color: #f9f9f9;
}

/* 決済ボタン */
.payment-actions {
  margin-top: 2rem;
}

.payment-button {
  width: 100%;
  padding: 1.25rem;
  background: linear-gradient(90deg, #ff6b9d 0%, #e91e63 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1.2rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.2s;
}

.payment-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(233, 30, 99, 0.3);
}

.payment-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ダイアログ */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.dialog-content {
  background-color: white;
  border-radius: 8px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.address-dialog-content {
  max-width: 600px;
}

.dialog-title {
  font-size: 1.5rem;
  font-weight: bold;
  color: #333;
  margin-bottom: 1.5rem;
}

.card-element {
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 1.5rem;
}

.dialog-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.dialog-button {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
}

.dialog-button.cancel {
  background-color: #f5f5f5;
  color: #666;
}

.dialog-button.cancel:hover {
  background-color: #e0e0e0;
}

.dialog-button.confirm {
  background-color: #e91e63;
  color: white;
}

.dialog-button.confirm:hover {
  background-color: #c2185b;
}

/* レスポンシブ */
@media (max-width: 768px) {
  .payment-layout {
    grid-template-columns: 1fr;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .page-subtitle {
    font-size: 1rem;
  }

  .order-summary,
  .payment-form {
    padding: 1.5rem;
  }

  .return-item-content {
    flex-direction: column;
  }

  .return-item-image {
    width: 100%;
    aspect-ratio: 640 / 360; /* 16:9の比率を維持 */
  }
}
</style>
